import type { FormEvent, KeyboardEvent } from 'react';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Input } from 'antd';
import type { InputRef } from 'antd';
import EditOutlined from '@ant-design/icons/EditOutlined';
import { useTranslation } from 'react-i18next';
import {
  INQUIRY_NAME_MAX,
  useNameError,
  validateInquiryName,
} from './InquiryDialogs';
import styles from './title.module.css';

export interface InquiryTitleHandle {
  /**
   * Start editing with the whole title selected (after New Q&A). `initial`
   * is the title to edit when the new Q&A may not be rendered yet.
   */
  edit: (initial?: string) => void;
}

interface InquiryTitleProps {
  name: string | null | undefined;
  disabled?: boolean;
  onRename: (name: string) => Promise<boolean>;
}

/** The selected Q&A's title in the toolbar; activate it to rename in place. */
const InquiryTitle = forwardRef<InquiryTitleHandle, InquiryTitleProps>(
  ({ name, disabled, onRename }, ref) => {
    const { t } = useTranslation();
    const errorText = useNameError();
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState(name ?? '');
    const [error, setError] = useState<string | null>(null);
    const input = useRef<InputRef>(null);
    const button = useRef<HTMLButtonElement>(null);

    useImperativeHandle(ref, () => ({
      edit: (initial?: string) => {
        setValue(initial ?? name ?? '');
        setError(null);
        setEditing(true);
      },
    }));

    useEffect(() => {
      if (!editing) setValue(name ?? '');
    }, [name, editing]);

    useEffect(() => {
      if (editing) {
        input.current?.focus();
        input.current?.select();
      }
    }, [editing]);

    const stop = () => {
      setEditing(false);
      setError(null);
      requestAnimationFrame(() => button.current?.focus());
    };

    const save = async (event?: FormEvent) => {
      event?.preventDefault();
      const result = validateInquiryName(value);
      if (result.error) {
        setError(errorText(result.error));
        return;
      }
      if (result.name === (name ?? '')) {
        stop();
        return;
      }
      if (await onRename(result.name)) stop();
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        stop();
      }
    };

    if (editing) {
      return (
        <form className={styles.form} onSubmit={save} noValidate>
          <Input
            ref={input}
            value={value}
            onChange={event => setValue(event.target.value)}
            onKeyDown={onKeyDown}
            onBlur={() => void save()}
            aria-label={t('v2.history.titleLabel')}
            aria-invalid={!!error}
            aria-describedby={error ? 'inquiry-title-error' : undefined}
            status={error ? 'error' : undefined}
            maxLength={INQUIRY_NAME_MAX * 2}
            size="large"
            data-testid="inquiry-title-input"
          />
          {error ? (
            <span
              id="inquiry-title-error"
              role="alert"
              className={styles.error}
            >
              {error}
            </span>
          ) : null}
        </form>
      );
    }

    return (
      <button
        ref={button}
        type="button"
        className={styles.title}
        disabled={disabled}
        onClick={() => {
          setValue(name ?? '');
          setEditing(true);
        }}
        aria-label={`${name || t('v2.history.noTitle')}${t('v2.common.paren', {
          text: t('v2.history.editTitle'),
        })}`}
        title={t('v2.history.editTitle')}
        data-testid="inquiry-title"
      >
        <span className={styles.text}>{name || t('v2.history.noTitle')}</span>
        {!disabled && <EditOutlined aria-hidden className={styles.icon} />}
      </button>
    );
  },
);

InquiryTitle.displayName = 'InquiryTitle';

export default InquiryTitle;
